<template>
  <div class="tags d-flex align-center justify-center">
    <v-menu
      v-model="menu"
      offset-y
      nudge-left="150"
      :close-on-content-click="false"
      @input="onMenuToggled"
    >
      <template #activator="{ on, attrs }">
        <div
          class="tags__selected-items position-relative w-100"
          :class="{
            'tags__selected-items--empty py-0': selectedTags.length === 0,
            'pr-4 d-flex align-center': selectedTags.length > 0,
          }"
          v-bind="attrs"
          v-on="on"
        >
          <slot name="placeholder"></slot>

          <TagChip
            v-for="tag in selectedTags"
            :key="tag.id"
            :tag="tag"
            :tooltip="tooltip"
            @click.stop="onSelectedTagClick(tag)"
          />

          <div
            v-if="showAddButton"
            class="tags__add-btn d-none"
            :class="{
              'position-absolute e-right-0': selectedTags.length > 0,
            }"
          >
            <slot name="add-btn">
              <v-btn icon x-small color="primary">
                <v-icon v-if="selectedTags.length === 0" x-small
                  >fa-plus</v-icon
                >
                <v-icon v-else x-small>fa-pen</v-icon>
              </v-btn>
            </slot>
          </div>
        </div>
      </template>

      <v-card class="tags__card">
        <v-text-field
          ref="searchInput"
          v-model="search"
          label="Filter tags"
          class="tags__search-field pa-3 px-4"
          hide-details
          dense
          solo
          flat
          outlined
          @hook:mounted="focusSearchInput"
        />

        <EToggleSwitch
          v-if="showInclusionOptions"
          v-model="inclusionMode"
          :options="inclusionOptions"
          size="sm"
          color="primary"
          class="ml-4 mb-2"
          :dark="false"
        />

        <v-list dense class="tags__list overflow-y-auto pa-0">
          <template v-if="filteredTags.length > 0">
            <v-list-item
              v-for="tag in filteredTags"
              :key="tag.name"
              class="tags__list-item pl-2"
              :class="{ 'tags__list-item--selected': isSelected(tag) }"
              @click="onTagClick(tag)"
            >
              <v-list-item-content>
                <div class="e-flex">
                  <div class="tags__list__icon e-flex e-justify-center pt-1">
                    <v-icon v-if="isSelected(tag)" color="#333">
                      fa-check
                    </v-icon>
                  </div>
                  <div>
                    <TagChip :tag="tag" list-item :is-excluded="isExclusion" />
                  </div>
                </div>
              </v-list-item-content>
            </v-list-item>
          </template>

          <v-list-item v-else>
            <v-list-item-content>
              No results for search: "{{ search }}"
            </v-list-item-content>
          </v-list-item>
        </v-list>

        <v-divider />

        <v-card-actions>
          <v-btn text @click="openEditor">Edit Tags</v-btn>
        </v-card-actions>
      </v-card>
    </v-menu>
    <v-dialog
      v-model="showTagEditor"
      max-width="600px"
      content-class="tags-editor-dialog overflow-hidden"
    >
      <TagEditor v-if="showTagEditor" />
    </v-dialog>
  </div>
</template>

<script lang="ts">
import Vue from "vue"
import { Tag } from "@evercam/api"
import TagChip from "@/components/tags/TagChip.vue"
import TagEditor from "@/components/tags/TagEditor.vue"
import { mapStores } from "pinia"
import { useTagStore } from "@/stores/tag"

export default Vue.extend({
  name: "TagSelector",
  components: {
    TagChip,
    TagEditor,
  },
  props: {
    tags: {
      type: Array as () => Tag[],
      default: () => [],
    },
    selectedTags: {
      type: Array as () => Tag[],
      default: () => [],
    },
    showAddButton: {
      type: Boolean,
      default: true,
    },
    showInclusionOptions: {
      type: Boolean,
      default: false,
    },
    closeOnContentClick: {
      type: Boolean,
      default: true,
    },
    closeOnClickOutside: {
      type: Boolean,
      default: true,
    },
    openOnSelectedTagClick: {
      type: Boolean,
      default: false,
    },
    tooltip: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      search: "",
      menu: false,
      isLoading: false,
      showTagEditor: false,
      inclusionMode: "∋",
      inclusionOptions: [
        { label: "Has Tag ∋ ", value: "∋" },
        { label: "Doesn't have Tag ∌", value: "∌" },
      ],
    }
  },
  computed: {
    ...mapStores(useTagStore),
    filteredTags(): Tag[] {
      return this.tags
        .filter((t) => {
          const matchedSearch = this.tagStore.matchTag(t, this.search)
          const isConflicting = this.selectedTags.some((tag) => {
            return (
              (this.isExclusion && !tag.isExcluded && tag.id === t.id) ||
              (!this.isExclusion && tag.isExcluded && tag.id === t.id)
            )
          })

          return matchedSearch && !isConflicting
        })
        .sort((a, b) => {
          const aSelected = this.isSelected(a)
          const bSelected = this.isSelected(b)

          if (aSelected && !bSelected) {
            return -1
          }
          if (!aSelected && bSelected) {
            return 1
          }

          return a.name.localeCompare(b.name)
        })
    },
    isExclusion(): boolean {
      return this.inclusionMode === "∌"
    },
  },
  watch: {
    menu(isVisible) {
      if (!isVisible) {
        this.$setTimeout(() => (this.search = ""), 500)
      }
    },
  },
  methods: {
    isSelected(tag: Tag): boolean {
      return this.selectedTags.some((t) => t.id === tag.id)
    },
    onMenuToggled(isVisible) {
      if (isVisible) {
        this.focusSearchInput()
      }
    },
    onSelectedTagClick(tag: Tag) {
      this.$emit("selected-tag-click", {
        ...tag,
        isExcluded: this.isExclusion,
      })

      if (this.openOnSelectedTagClick) {
        this.menu = true
      } else {
        this.maybeCloseMenu()
      }
    },
    onTagClick(tag: Tag) {
      this.$emit("tag-click", {
        ...tag,
        isExcluded: this.isExclusion,
      })
      this.maybeCloseMenu()
    },
    maybeCloseMenu() {
      if (this.closeOnContentClick) {
        this.menu = false
      }
    },
    focusSearchInput() {
      if (!this.$refs.searchInput || !this.menu) {
        return
      }

      this.$refs.searchInput.focus()
      if (!this.$refs.searchInput.isFocused) {
        this.$setTimeout(this.focusSearchInput)
      }
    },
    openEditor() {
      this.showTagEditor = true
      this.menu = false
    },
  },
})
</script>

<style lang="scss">
$border: 1px solid var(--dash-border-color) !important;

.tags {
  &__card {
    width: 400px;
    border: $border;
  }
  &__list {
    border-top: $border;
    max-height: 300px;

    &__icon {
      min-width: 24px;
      height: fit-content;
    }

    .v-list-item {
      border-bottom: $border;
      &:hover,
      &.tags__list-item--selected {
        &::before {
          opacity: 0.125;
          background-color: var(--v-primary-base);
        }
        .tag__description {
          &--list-item {
            color: var(--v-on_background-base);
          }
        }
      }
    }
  }

  &__selected-items {
    display: flex;
    flex-wrap: wrap;
    &:not(.tags__selected-items--empty) {
      min-height: 30px;
    }
    .tag {
      margin-bottom: 2px;
    }
  }

  &__add-btn {
    &.v-btn.v-size--x-small {
      height: 15px;
      width: 18px;
      border-radius: 3px;
    }
    &.v-btn--icon.v-size--x-small .v-icon {
      height: 15px;
    }
    &::before {
      opacity: 0.08;
    }
    .v-btn__content > i {
      font-size: 0.65rem !important;
    }
  }

  &:hover {
    .tags__add-btn {
      display: block !important;
    }
  }
}

table tr:hover {
  .tags__add-btn {
    display: block !important;
  }
}

.tags-editor-dialog {
  height: 650px;
}
</style>
